import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
    Alert,
} from "@mui/material";
import { FormProvider, RHFSelect, RHFTextField } from "../hook-form";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import axiosInstance from "src/utils/axios";
import { useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useAuth from "src/hooks/useAuth";

interface DebtClearanceDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function DebtClearanceDialog({ open, onClose }: DebtClearanceDialogProps) {
    const { contracts, selectedContract } = useAuth();
    const theme = useTheme();
    const maxYear = new Date().getFullYear() - 1;
    const schema = yup.object().shape({
        contract: yup.number().required("Selecione um contrato"),
        year: yup
            .number()
            .typeError("Digite um ano válido")
            .integer("Digite um ano válido")
            .min(1980, "Digite um ano válido")
            .max(maxYear, "O ano deve ser anterior ao ano atual")
            .required("Digite o ano de referência"),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            contract: selectedContract?.id,
            year: "",
        },
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleNumChange = (e: any) => {
        const limit = 4;
        e.target.value = e.target.value.slice(0, limit);
    };

    const onSubmit = async (data: any) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.post("/users/declaration/debt-clearance", {
                contract_id: data.contract,
                year: data.year,
            });

            const linkDocument = response.data.url;

            // Tenta abrir em uma nova aba
            const newWindow = window.open(linkDocument, "_blank");

            // Se o popup foi bloqueado, mostra uma mensagem para o usuário
            if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
                toast.warning(
                    "O bloqueador de popup está ativo. Por favor, permita popups para este site ou cole o link no seu navegador."
                );

                shareLink(linkDocument);
            }

            methods.reset();
            onClose();
            toast.success("Documento gerado com sucesso!");
        } catch (error: any) {
            const message =
                error.message ||
                "Ocorreu um erro ao gerar o documento. Tente novamente ou entre em contato com a Central de Atendimento.";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    function shareLink(link: string) {
        navigator.clipboard.writeText(link);
        toast.success("Link copiado para a área de transferência");
    }

    const capitalizeWords = (str: string) => {
        return str
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs">
            <DialogTitle>
                <Typography
                    color={theme.palette.primary.main}
                    fontWeight="bold"
                    fontFamily="Open Sans"
                    fontSize="16px"
                    marginRight="8px"
                >
                    Declaração Anual de Quitação de Débitos
                </Typography>
                <Typography fontSize="14px" color={theme.palette.text.secondary} mt={1}>
                    A declaração anual de quitação é o documento no qual consta a informação de
                    quitação dos débitos compreendendo os meses de janeiro a dezembro de cada ano.
                </Typography>
            </DialogTitle>
            <DialogContent style={{ marginTop: "15px" }}>
                <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                    <Stack spacing={3} pt={2}>
                        <RHFSelect name="contract" id="contract" label="Contrato *">
                            {contracts.map((contract) => (
                                <option
                                    key={`${contract.id}-${contract.contract_number}`}
                                    value={contract.id}
                                >
                                    {capitalizeWords(contract.name)} • {contract.contract_number}
                                </option>
                            ))}
                        </RHFSelect>
                        <RHFTextField
                            name="year"
                            id="year"
                            label="Ano de Referência *"
                            type="number"
                            placeholder="Ex.: 2024"
                            inputProps={{
                                maxLength: 4,
                                onChange: handleNumChange,
                            }}
                        />
                        {error && <Alert severity="error">{error}</Alert>}
                        <Box display="flex" justifyContent="center" gap={2}>
                            <Button variant="text" fullWidth onClick={onClose} type="button">
                                Cancelar
                            </Button>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                fullWidth
                                type="submit"
                                loading={loading}
                            >
                                Gerar documento
                            </LoadingButton>
                        </Box>
                    </Stack>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
}





